// ===========================================
// Student Bookings List (admin)
// ===========================================
// Shows a student's hybrid bookings inside the admin View Student dialog,
// with reschedule and cancel actions.

import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Cancel as CancelIcon,
  EditCalendar as EditCalendarIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  SwapHoriz as SwapHorizIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

import {
  HybridBooking,
  TimeSlot,
  formatTimeSlot,
} from '../../../api/hybridBooking.api';
import {
  useAdminStudentBookings,
  useAdminAvailableSlots,
  useAdminRescheduleBooking,
  useAdminCancelBooking,
} from '../../../hooks/useHybridBooking';
import { parseUTCDate } from '../../../utils/dateUtils';
import SlotPicker from '../../booking/SlotPicker';
import WeekTilePicker from '../../booking/WeekTilePicker';
import ChangeClinicTeacherDialog from '../hybridBooking/ChangeClinicTeacherDialog';

interface Props {
  studentId: string;
}

/** The teacher a clinic resolves to: its override, else the lesson's teacher. */
function resolvedTeacher(b: HybridBooking): { id?: string; name?: string; isOverride: boolean } {
  if (b.OverrideTeacher) {
    return {
      id: b.OverrideTeacher.id,
      name: `${b.OverrideTeacher.User.firstName} ${b.OverrideTeacher.User.lastName}`,
      isOverride: true,
    };
  }
  const lesson = b.Lesson;
  const u = lesson?.Instructor?.User;
  return { id: lesson?.teacherId, name: u ? `${u.firstName} ${u.lastName}` : undefined, isOverride: false };
}

interface RescheduleState {
  open: boolean;
  booking: HybridBooking | null;
}

interface CancelState {
  open: boolean;
  booking: HybridBooking | null;
  reason: string;
}

function getLesson(b: HybridBooking) {
  return b.Lesson;
}

export default function StudentBookingsList({ studentId }: Props) {
  const { data: bookings, isLoading } = useAdminStudentBookings(studentId);

  const [reschedule, setReschedule] = useState<RescheduleState>({
    open: false,
    booking: null,
  });
  const [cancel, setCancel] = useState<CancelState>({
    open: false,
    booking: null,
    reason: '',
  });
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  // Per-booking Change Teacher (2026-09-06).
  const [teacherFor, setTeacherFor] = useState<HybridBooking | null>(null);
  // Admins may move a 1:1 clinic into any week of the course, not just its own
  // booked week. Defaults to the booking's current week when the dialog opens.
  const [selectedWeek, setSelectedWeek] = useState<number>(0);

  const rescheduleMutation = useAdminRescheduleBooking();
  const cancelMutation = useAdminCancelBooking();

  const rescheduleLessonId = reschedule.booking?.lessonId || '';

  // Term bounds drive the week tiles (week 1 .. last term week, with date
  // ranges). Carried on the booking's lesson via the API include.
  const rescheduleTerm = reschedule.booking
    ? getLesson(reschedule.booking)?.Term
    : undefined;

  const { data: availableSlots, isLoading: slotsLoading } = useAdminAvailableSlots(
    rescheduleLessonId,
    selectedWeek,
    reschedule.booking?.id
  );

  // Hide the booking's existing slot from the picker so admins don't pick the same time
  const filteredSlots = useMemo<TimeSlot[] | undefined>(() => {
    if (!availableSlots || !reschedule.booking) return availableSlots;
    const currentDateKey = reschedule.booking.scheduledDate?.split('T')[0];
    return availableSlots.filter(
      (s) =>
        !(
          s.dateKey === currentDateKey &&
          s.startTime === reschedule.booking!.startTime &&
          s.endTime === reschedule.booking!.endTime
        )
    );
  }, [availableSlots, reschedule.booking]);

  const upcomingBookings = useMemo(
    () =>
      (bookings || []).filter(
        (b) => b.status !== 'CANCELLED' && b.status !== 'COMPLETED'
      ),
    [bookings]
  );

  const handleOpenReschedule = (booking: HybridBooking) => {
    setSelectedSlot(null);
    setSelectedWeek(booking.weekNumber || 0);
    setReschedule({ open: true, booking });
  };

  const handleCloseReschedule = () => {
    setReschedule({ open: false, booking: null });
    setSelectedSlot(null);
    setSelectedWeek(0);
  };

  // Clearing the chosen slot whenever the week changes prevents confirming a
  // slot that belongs to a week no longer being viewed.
  const handleWeekChange = (week: number) => {
    setSelectedWeek(week);
    setSelectedSlot(null);
  };

  const handleConfirmReschedule = () => {
    if (!reschedule.booking || !selectedSlot) return;
    const dateKey =
      selectedSlot.dateKey ??
      (selectedSlot.date
        ? new Date(selectedSlot.date).toISOString().split('T')[0]
        : '');
    rescheduleMutation.mutate(
      {
        id: reschedule.booking.id,
        data: {
          scheduledDate: `${dateKey}T00:00:00.000Z`,
          startTime: selectedSlot.startTime,
          endTime: selectedSlot.endTime,
        },
      },
      { onSuccess: () => handleCloseReschedule() }
    );
  };

  const handleOpenCancel = (booking: HybridBooking) => {
    setCancel({ open: true, booking, reason: '' });
  };

  const handleCloseCancel = () => {
    setCancel({ open: false, booking: null, reason: '' });
  };

  const handleConfirmCancel = () => {
    if (!cancel.booking) return;
    cancelMutation.mutate(
      {
        id: cancel.booking.id,
        reason: cancel.reason.trim() || undefined,
      },
      { onSuccess: () => handleCloseCancel() }
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!upcomingBookings.length) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography color="text.secondary">No upcoming bookings.</Typography>
      </Paper>
    );
  }

  return (
    <>
      <Stack spacing={1}>
        {upcomingBookings.map((booking) => {
          const lesson = getLesson(booking);
          return (
            <Paper
              key={booking.id}
              variant="outlined"
              sx={{
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight="medium" noWrap>
                  {lesson?.name || 'Lesson'}
                  {booking.weekNumber ? ` — Week ${booking.weekNumber}` : ''}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    mt: 0.25,
                    color: 'text.secondary',
                  }}
                >
                  <ScheduleIcon sx={{ fontSize: 14 }} />
                  <Typography variant="caption">
                    {format(parseUTCDate(booking.scheduledDate), 'EEE, d MMM yyyy')}{' '}
                    · {formatTimeSlot(booking.startTime, booking.endTime)}
                  </Typography>
                </Box>
                {(() => {
                  const t = resolvedTeacher(booking);
                  if (!t.name) return null;
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, color: 'text.secondary' }}>
                      <PersonIcon sx={{ fontSize: 14 }} />
                      <Typography variant="caption">
                        Teacher: {t.name}
                        {t.isOverride ? ' (covering)' : ''}
                      </Typography>
                    </Box>
                  );
                })()}
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="Change teacher">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Change teacher"
                      onClick={() => setTeacherFor(booking)}
                    >
                      <SwapHorizIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Reschedule">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenReschedule(booking)}
                    >
                      <EditCalendarIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Cancel booking">
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleOpenCancel(booking)}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Paper>
          );
        })}
      </Stack>

      {/* Reschedule Dialog */}
      <Dialog
        open={reschedule.open}
        onClose={handleCloseReschedule}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Reschedule Booking
          {selectedWeek ? ` — Week ${selectedWeek}` : ''}
        </DialogTitle>
        <DialogContent>
          {reschedule.booking && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1">
                {getLesson(reschedule.booking)?.name || 'Lesson'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Current: Week {reschedule.booking.weekNumber} ·{' '}
                {format(parseUTCDate(reschedule.booking.scheduledDate), 'EEE, d MMM')}{' '}
                ·{' '}
                {formatTimeSlot(
                  reschedule.booking.startTime,
                  reschedule.booking.endTime
                )}
              </Typography>
            </Box>
          )}
          <Divider sx={{ my: 2 }} />
          {rescheduleTerm && (
            <>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Week
              </Typography>
              <WeekTilePicker
                termStartDate={rescheduleTerm.startDate}
                termEndDate={rescheduleTerm.endDate}
                selectedWeek={selectedWeek}
                onWeekSelect={handleWeekChange}
                currentWeek={reschedule.booking?.weekNumber}
              />
            </>
          )}
          <Typography variant="subtitle2" gutterBottom>
            Select new time slot
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Admin reschedule bypasses bookings-open and 24-hour notice rules. You can
            move this clinic into any week of the term.
          </Alert>
          <SlotPicker
            slots={filteredSlots}
            selectedSlot={selectedSlot}
            onSlotSelect={setSelectedSlot}
            isLoading={slotsLoading}
            emptyMessage="No available slots for this week."
            confirmationPrefix="New time:"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReschedule}>Close</Button>
          <Button
            variant="contained"
            onClick={handleConfirmReschedule}
            disabled={!selectedSlot || rescheduleMutation.isPending}
          >
            {rescheduleMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              'Confirm Reschedule'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Teacher Dialog */}
      {teacherFor && (
        <ChangeClinicTeacherDialog
          open
          onClose={() => setTeacherFor(null)}
          bookingId={teacherFor.id}
          lessonId={teacherFor.lessonId}
          currentTeacherId={resolvedTeacher(teacherFor).id}
          currentTeacherName={resolvedTeacher(teacherFor).name}
          weekNumber={teacherFor.clinicWeekNumber ?? teacherFor.weekNumber}
          scheduledDate={teacherFor.scheduledDate}
          startTime={teacherFor.startTime}
          endTime={teacherFor.endTime}
        />
      )}

      {/* Cancel Dialog */}
      <Dialog open={cancel.open} onClose={handleCloseCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel Booking</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to cancel this booking?
          </Typography>
          {cancel.booking && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {getLesson(cancel.booking)?.name} — Week {cancel.booking.weekNumber}
              <br />
              {format(parseUTCDate(cancel.booking.scheduledDate), 'EEE, d MMM')} ·{' '}
              {formatTimeSlot(cancel.booking.startTime, cancel.booking.endTime)}
            </Typography>
          )}
          <TextField
            fullWidth
            label="Reason (optional)"
            value={cancel.reason}
            onChange={(e) =>
              setCancel((prev) => ({ ...prev, reason: e.target.value }))
            }
            multiline
            rows={2}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancel}>Keep Booking</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmCancel}
            disabled={cancelMutation.isPending}
          >
            {cancelMutation.isPending ? <CircularProgress size={20} /> : 'Cancel Booking'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
